import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import Button from './Button';
import { useAlert } from '../../providers/AlertProvider';
import { useAuthProvider } from '../../providers/AuthProvider';
import { getSessionUUID } from '../lib/AuthToken';

const AlcoholTestingSystem = ({ theme }) => {
    const [testDecision, setTestDecision] = useState(null); // 'run' or 'skip'
    const [isGenerating, setIsGenerating] = useState(false);
    const [drivers, setDrivers] = useState([]);
    const [selectedDriver, setSelectedDriver] = useState('');
    const [bacReading, setBacReading] = useState('');
    const [testResult, setTestResult] = useState('pass');
    const [comments, setComments] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showTestForm, setShowTestForm] = useState(false);

    const { showAlert } = useAlert();
    const { isDev, permissions, profile } = useAuthProvider();
    const canView = isDev || (permissions && permissions.perm_alcohol_view);

    useEffect(() => {
        fetchDrivers();
    }, []);

    const fetchDrivers = async () => {
        try {
            const sessionToken = await getSessionUUID();
            const response = await fetch('/alcohol_testing/get_drivers', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setDrivers(data.drivers || []);
            }
        } catch (error) {
            console.error('Error fetching drivers:', error);
        }
    };

    const generateTestDecision = async () => {
        setIsGenerating(true);
        
        try {
            // Simulate checking if test should be run
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            // Get configuration to determine test frequency
            const sessionToken = await getSessionUUID();
            const response = await fetch('/alcohol_testing/get_config', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                }
            });

            let shouldRunTest = false;
            if (response.ok) {
                const data = await response.json();
                // pass_percentage means % that pass WITHOUT testing
                // So if pass_percentage = 80%, only 20% should be tested
                const passPercentage = data.pass_percentage || 80; // Default 80% pass without testing
                const testFrequency = 100 - passPercentage; // If 80% pass, then 20% get tested
                const random = Math.random() * 100;
                shouldRunTest = random <= testFrequency;
            } else {
                // Default: 80% pass without testing, so 20% get tested
                shouldRunTest = Math.random() * 100 <= 20;
            }

            setTestDecision(shouldRunTest ? 'run' : 'skip');
            setShowTestForm(shouldRunTest);
            
        } catch (error) {
            console.error('Error generating test decision:', error);
            setTestDecision('skip'); // Default to skip on error
        } finally {
            setIsGenerating(false);
        }
    };

    const submitTest = async () => {
        if (!selectedDriver || !bacReading) {
            showAlert({
                message: 'Please select a driver and enter BAC reading',
                type: 'warning',
                buttons: [{ label: 'Close' }]
            });
            return;
        }

        const bacValue = parseFloat(bacReading);
        if (isNaN(bacValue) || bacValue < 0 || bacValue > 0.500) {
            showAlert({
                message: 'Please enter a valid BAC reading between 0.000 and 0.500',
                type: 'warning',
                buttons: [{ label: 'Close' }]
            });
            return;
        }

        setIsSubmitting(true);

        try {
            const sessionToken = await getSessionUUID();
            const response = await fetch('/alcohol_testing/submit_test', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
                body: JSON.stringify({
                    profile_id: selectedDriver,
                    bac_reading: bacValue,
                    comments: comments || `Manual ${testResult.toUpperCase()} result entered`,
                    created_by: profile?.id
                })
            });

            if (response.ok) {
                showAlert({
                    message: 'Test recorded successfully!',
                    type: 'success',
                    buttons: [{ label: 'Close' }]
                });
                resetSystem();
            } else {
                const errorData = await response.json();
                showAlert({
                    message: `Failed to record test: ${errorData.error}`,
                    type: 'danger',
                    buttons: [{ label: 'Close' }]
                });
            }
        } catch (error) {
            console.error('Error submitting test:', error);
            showAlert({
                message: 'Failed to record test',
                type: 'danger',
                buttons: [{ label: 'Close' }]
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetSystem = () => {
        setTestDecision(null);
        setShowTestForm(false);
        setSelectedDriver('');
        setBacReading('');
        setTestResult('pass');
        setComments('');
    };

    const getTrafficLightColor = () => {
        if (isGenerating) return '#FFA500'; // Orange while generating
        if (testDecision === 'run') return '#DC3545'; // Red - run test
        if (testDecision === 'skip') return '#28A745'; // Green - skip test
        return '#6C757D'; // Gray - no decision yet
    };

    const getStatusText = () => {
        if (isGenerating) return 'CHECKING...';
        if (testDecision === 'run') return 'RUN ALCOHOL TEST';
        if (testDecision === 'skip') return 'NO TEST REQUIRED';
        return 'CLICK TO CHECK';
    };

    return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
            <h2 style={{ marginBottom: '30px' }}>Random Alcohol/Drugs Testing System</h2>
            
            {/* Large Centered Traffic Light */}
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                flexDirection: 'column',
                marginBottom: '40px'
            }}>
                <div
                    onClick={canView && !isGenerating && !testDecision ? generateTestDecision : null}
                    style={{
                        width: '200px',
                        height: '200px',
                        borderRadius: '50%',
                        backgroundColor: getTrafficLightColor(),
                        border: '8px solid #333',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: canView && !isGenerating && !testDecision ? 'pointer' : 'default',
                        boxShadow: '0 0 20px rgba(0,0,0,0.3)',
                        transition: 'all 0.3s ease',
                        marginBottom: '20px'
                    }}
                >
                    {isGenerating && (
                        <div style={{
                            width: '40px',
                            height: '40px',
                            border: '4px solid #ffffff',
                            borderTop: '4px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                        }} />
                    )}
                </div>
                
                <h3 style={{ 
                    color: getTrafficLightColor(),
                    fontWeight: 'bold',
                    marginBottom: '10px'
                }}>
                    {getStatusText()}
                </h3>

                {!canView && (
                    <p style={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                        ⚠ View-Only: You do not have permission to log alcohol tests.
                    </p>
                )}

                {testDecision === 'skip' && (
                    <p style={{ color: '#28A745', fontSize: '18px', marginBottom: '20px' }}>
                        ✅ No alcohol test required at this time
                    </p>
                )}
            </div>

            {/* Test Form - Only show when test should be run */}
            {showTestForm && (
                <div style={{
                    maxWidth: '600px',
                    margin: '0 auto',
                    padding: '30px',
                    border: '2px solid #DC3545',
                    borderRadius: '10px',
                    backgroundColor: theme === 'dark' ? '#2a2a2a' : '#fff'
                }}>
                    <h4 style={{ color: '#DC3545', marginBottom: '25px' }}>
                        🚨 ALCOHOL TEST REQUIRED 🚨
                    </h4>

                    <div style={{ marginBottom: '20px' }}>
                        <Form.Label style={{ fontWeight: 'bold' }}>Select Driver/Person:</Form.Label>
                        <Form.Select
                            value={selectedDriver}
                            onChange={(e) => setSelectedDriver(e.target.value)}
                            disabled={isSubmitting}
                            style={{ marginTop: '5px' }}
                        >
                            <option value="">Choose a person...</option>
                            {drivers.map((driver) => (
                                <option key={driver.id} value={driver.id}>
                                    {driver.name} {driver.surname || ''} ({driver.group?.toUpperCase()})
                                </option>
                            ))}
                        </Form.Select>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <Form.Label style={{ fontWeight: 'bold' }}>BAC Reading:</Form.Label>
                        <Form.Control
                            type="number"
                            step="0.001"
                            min="0"
                            max="0.500"
                            placeholder="0.000"
                            value={bacReading}
                            onChange={(e) => setBacReading(e.target.value)}
                            disabled={isSubmitting}
                            style={{ marginTop: '5px' }}
                        />
                        <small style={{ color: '#666' }}>
                            Enter BAC reading (0.000 - 0.500). Legal limit: 0.080
                        </small>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <Form.Label style={{ fontWeight: 'bold' }}>Test Result:</Form.Label>
                        <div style={{ marginTop: '10px' }}>
                            <Form.Check
                                type="radio"
                                name="testResult"
                                value="pass"
                                label="✅ PASS (Under 0.080)"
                                checked={testResult === 'pass'}
                                onChange={(e) => setTestResult(e.target.value)}
                                disabled={isSubmitting}
                                style={{ marginBottom: '10px' }}
                            />
                            <Form.Check
                                type="radio"
                                name="testResult"
                                value="fail"
                                label="❌ FAIL (0.080 or above)"
                                checked={testResult === 'fail'}
                                onChange={(e) => setTestResult(e.target.value)}
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>

                    <div style={{ marginBottom: '25px' }}>
                        <Form.Label style={{ fontWeight: 'bold' }}>Comments (Optional):</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Any additional notes about the test..."
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            disabled={isSubmitting}
                            style={{ marginTop: '5px' }}
                        />
                    </div>

                    <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
                        <Button
                            variant="success"
                            onClick={submitTest}
                            disabled={isSubmitting || !selectedDriver || !bacReading}
                        >
                            {isSubmitting ? '📝 Recording...' : '📝 Submit Test Result'}
                        </Button>
                        
                        <Button
                            variant="secondary"
                            onClick={resetSystem}
                            disabled={isSubmitting}
                        >
                            🔄 Reset
                        </Button>
                    </div>
                </div>
            )}

            {/* Reset Button when test is skipped */}
            {testDecision === 'skip' && (
                <Button
                    variant="primary"
                    onClick={resetSystem}
                    style={{ marginTop: '20px', width : '150px', display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
                >
                    🔄 Check Again
                </Button>
            )}

            <style jsx>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default AlcoholTestingSystem;
